import { useCallback, useMemo } from "react";
import { Card, BlockStack, Text, FormLayout, Bleed } from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import useLanguages from "@shared/hooks/common/useLanguages";
import { MultiSelectCombobox } from "@shared/ui";
import { Instructor } from "@features/events/types";

interface CourseLanguageSelectionCardProps {
  instructorData: Partial<Instructor>;
  onDataChange: (updates: Partial<Instructor>) => void;
  fieldErrors: Record<string, string | undefined>;
}

export default function CourseLanguageSelectionCard({
  instructorData,
  onDataChange,
  fieldErrors,
}: CourseLanguageSelectionCardProps) {
  const { t } = useTranslation();
  const { categoryOptions, isLoading: isLoadingMetadata } = useEventMetadata();
  const { languages, isLoading: isLoadingLanguages } = useLanguages();

  // Get available languages from useLanguages hook
  const languageOptions = useMemo(() => {
    return languages.map((language) => ({
      label: language.englishLabel,
      value: language.iso,
    }));
  }, [languages]);

  const handleLanguageChange = useCallback(
    (selectedLanguages: string[]) => {
      onDataChange({ languageCodes: selectedLanguages });
    },
    [onDataChange]
  );

  const handleTopicsChange = useCallback(
    (selectedTopics: string[]) => {
      onDataChange({ categoryIds: selectedTopics });
    },
    [onDataChange]
  );

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">
          {t("instructorDetails.languages.title")}
        </Text>

        <FormLayout>
          {/* Languages Section */}
          <BlockStack gap="100">
            <Text variant="bodyMd" as="p" requiredIndicator>
              {t("instructorDetails.languages.select.title")}
            </Text>
            {isLoadingLanguages ? (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("common.loading")}
              </Text>
            ) : (
              <MultiSelectCombobox
                options={languageOptions}
                selected={instructorData.languageCodes || []}
                onChange={handleLanguageChange}
                placeholder={t(
                  "instructorDetails.languages.select.placeholder"
                )}
                error={fieldErrors.languageCodes}
              />
            )}
            <Bleed marginBlockStart="050">
              <Text as="p" variant="bodySm" tone="subdued">
                {t("instructorDetails.languages.select.helpText")}
              </Text>
            </Bleed>
          </BlockStack>

          {/* Topics Section */}
          <BlockStack gap="100">
            <Text variant="bodyMd" as="p" requiredIndicator>
              {t("instructorDetails.topics.select.title")}
            </Text>

            {isLoadingMetadata ? (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("common.loading")}
              </Text>
            ) : (
              <MultiSelectCombobox
                options={categoryOptions}
                selected={instructorData.categoryIds || []}
                onChange={handleTopicsChange}
                placeholder={t("instructorDetails.topics.select.placeholder")}
                error={fieldErrors.categoryIds}
                maxSelected={4}
              />
            )}
            <Bleed marginBlockStart="050">
              <Text as="p" variant="bodySm" tone="subdued">
                {t("instructorDetails.topics.select.helpText", {
                  min: 1,
                  max: 4,
                  defaultValue:
                    "Select at least one and at most four categories. These determine where your instructor will be listed.",
                })}
              </Text>
            </Bleed>
          </BlockStack>
        </FormLayout>
      </BlockStack>
    </Card>
  );
}
